import type { Access } from '../../auth/access';
import type { KpiTone, KpiUnit } from './dashboards.api';

/* ── Numbers ─────────────────────────────────────────────────────────────── */

const inr = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
const num = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 });

/** Full value with its unit: ₹12,34,567 · 12.5h · 3d · 45% · 1,284. `null` reads as an em dash. */
export function formatValue(value: unknown, unit?: KpiUnit | null): string {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  switch (unit) {
    case 'INR':
      return inr.format(n);
    case 'HOURS':
      return `${num.format(n)}h`;
    case 'DAYS':
      return `${num.format(n)}d`;
    case 'PERCENT':
      return `${num.format(n)}%`;
    default:
      return num.format(n);
  }
}

/** Short form for axis ticks: ₹1.2L, ₹3.4Cr, 12K. */
export function formatCompact(n: number, unit?: KpiUnit | null): string {
  const abs = Math.abs(n);
  let s: string;
  if (unit === 'INR') {
    if (abs >= 1e7) s = `${+(n / 1e7).toFixed(1)}Cr`;
    else if (abs >= 1e5) s = `${+(n / 1e5).toFixed(1)}L`;
    else if (abs >= 1e3) s = `${+(n / 1e3).toFixed(1)}K`;
    else s = `${+n.toFixed(0)}`;
    return `₹${s}`;
  }
  if (abs >= 1e6) s = `${+(n / 1e6).toFixed(1)}M`;
  else if (abs >= 1e3) s = `${+(n / 1e3).toFixed(1)}K`;
  else s = `${+n.toFixed(1)}`;
  if (unit === 'HOURS') return `${s}h`;
  if (unit === 'DAYS') return `${s}d`;
  if (unit === 'PERCENT') return `${s}%`;
  return s;
}

/** Clean axis ticks from 0 to a round maximum at or above `max`. */
export function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0)) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const top = Math.ceil(max / step) * step;
  const ticks: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) ticks.push(+v.toFixed(10));
  return ticks;
}

/* ── Colour ──────────────────────────────────────────────────────────────── */

/**
 * Categorical series colours, assigned in this fixed order (never cycled): the validated
 * dataviz reference palette for a light surface. Colour follows the series' position in the
 * server's list, so a series keeps its colour wherever it appears.
 * Slots 3–5 sit below 3:1 on white, so every chart keeps a legend and a table view.
 */
export const SERIES_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948'];
/** A ninth-and-later series folds into this neutral rather than a generated hue. */
export const OTHER_COLOR = '#898781';

export const seriesColor = (i: number): string => SERIES_COLORS[i] ?? OTHER_COLOR;

/** Chart chrome (hairline grid, baseline, muted axis text). */
export const CHROME = { grid: '#e1e0d9', baseline: '#c3c2b7', muted: '#6f6e69', surface: '#ffffff' };

/** Tone → theme palette key and an icon, so a tone never relies on colour alone. */
export const TONES: Record<KpiTone, { color: string; icon: string | null; label: string }> = {
  GOOD: { color: 'success.main', icon: 'mdi:check-circle-outline', label: 'Good' },
  WARN: { color: 'warning.main', icon: 'mdi:alert-outline', label: 'Needs attention' },
  BAD: { color: 'error.main', icon: 'mdi:alert-octagon-outline', label: 'Problem' },
  NEUTRAL: { color: 'text.secondary', icon: null, label: '' },
};

export const toneOf = (t: KpiTone | null | undefined) => TONES[t ?? 'NEUTRAL'] ?? TONES.NEUTRAL;

/* ── Links ───────────────────────────────────────────────────────────────── */

/**
 * True when the signed-in user can open an admin-panel path: admin can open everything; others
 * when the path equals or sits under a granted menu path. `/dashboards/<module>` opens when the
 * user has any screen of that module.
 */
export function canOpen(link: string | null | undefined, access: Access | null): boolean {
  if (!link || !access) return false;
  if (access.admin) return true;
  const path = link.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
  const dash = /^\/dashboards\/([^/]+)$/.exec(path);
  if (dash) return access.menus.some((m) => m.moduleCode === dash[1]);
  return access.menus.some((m) => {
    if (!m.path) return false;
    const p = m.path.replace(/\/+$/, '') || '/';
    if (p === '/') return path === '/';
    return path === p || path.startsWith(`${p}/`);
  });
}
